// src/components/BoardPackConfiguration/BoardPackConfigurationPage.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiCall } from "@/api/apiCall";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ReactSelect from "@/components/common/ReactSelect";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Save,
  GripVertical,
  Settings,
  AlertCircle,
  CheckCircle2,
  Copy,
  RotateCcw,
  Code2,
  Search,
  X,
  Undo2,
  Redo2,
  Eye,
  SlidersHorizontal,
  ChevronLeft,
} from "lucide-react";
import {
  TypeSpecificFields,
  StableFocusInput as SFI,
  SubContractsEditor,
  ToggleRow,
} from "@/components/BoardPackConfiguration/TypeSpecificFields";
import PagePreview from "./PagePreview";
import { CATEGORY_COLORS, CATEGORY_DOT, DEFAULT_PARAMS, PAGE_TYPE_MAP, PAGE_TYPES } from "./data";

// ─── Undo / Redo hook ─────────────────────────────────────────────────────────
function useHistory(initial) {
  const [hist, setHist] = useState({ past: [], present: initial, future: [] });

  const set = useCallback((next) => {
    setHist((h) => ({
      past: [...h.past.slice(-49), h.present],
      present: typeof next === "function" ? next(h.present) : next,
      future: [],
    }));
  }, []);

  const undo = useCallback(
    () =>
      setHist((h) => {
        if (!h.past.length) return h;
        const prev = h.past[h.past.length - 1];
        return { past: h.past.slice(0, -1), present: prev, future: [h.present, ...h.future] };
      }),
    []
  );

  const redo = useCallback(
    () =>
      setHist((h) => {
        if (!h.future.length) return h;
        return { past: [...h.past, h.present], present: h.future[0], future: h.future.slice(1) };
      }),
    []
  );

  return {
    pages: hist.present,
    setPages: set,
    undo,
    redo,
    canUndo: hist.past.length > 0,
    canRedo: hist.future.length > 0,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function cleanPage({ _id, ...page }) {
  const cleaned = { ...page };
  if (
    cleaned.sub_contracts === undefined ||
    cleaned.sub_contracts === null ||
    (Array.isArray(cleaned.sub_contracts) && cleaned.sub_contracts.length === 0)
  ) {
    delete cleaned.sub_contracts;
  }
  return cleaned;
}

function buildJson(meta, pages) {
  return JSON.stringify({ ...meta, config: { pages: pages.map(cleanPage) } }, null, 2);
}

const StableFocusInput = SFI;

const BASE_WIDTH = 1280;
const BASE_HEIGHT = 720;

// ─── Resizable Sidebar Hook ───────────────────────────────────────────────────
function useResizable({ initialWidth, minWidth, maxWidth, side }) {
  const [width, setWidth] = useState(initialWidth);

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;

      const onMove = (e) => {
        const delta = side === "left" ? e.clientX - startX : startX - e.clientX;
        const newW = Math.min(Math.max(startW + delta, minWidth), maxWidth);
        setWidth(newW);
      };

      const onUp = () => {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [width, minWidth, maxWidth, side]
  );

  return { width, startResize };
}

// ─── Main Component ───────────────────────────────────────────────────────────
const BoardPackConfigurationPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [boardpackMeta, setBoardpackMeta] = useState({
    boardpack_key: "",
    boardpack_name: "",
    contract: "",
    status: "ACTIVE",
    created_by: 1,
  });
  const [selectedPageIdx, setSelectedPageIdx] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);

  // Hide/Show sidebars
  const [showLeftSidebar, setShowLeftSidebar] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);

  // ── Resizable sidebars ──────────────────────────────────────────────────────
  const { width: leftWidth, startResize: startLeftResize } = useResizable({
    initialWidth: 256,
    minWidth: 180,
    maxWidth: 420,
    side: "left",
  });

  const { width: rightWidth, startResize: startRightResize } = useResizable({
    initialWidth: 320,
    minWidth: 220,
    maxWidth: 500,
    side: "right",
  });

  const dragIdxRef = useRef(null);
  const savedSnapRef = useRef(null);

  // ── Scaled preview ────────────────────────────────────────────────────────────
  const [previewZoom, setPreviewZoom] = useState(1);
  const [contentNaturalH, setContentNaturalH] = useState(BASE_HEIGHT);
  const previewStateRef = useRef({ containerW: 0, containerH: 0, contentH: BASE_HEIGHT });
  const outerRoRef = useRef(null);
  const innerRoRef = useRef(null);

  const calcPreviewZoom = useCallback(() => {
    const { containerW, containerH, contentH } = previewStateRef.current;
    if (containerW > 0 && containerH > 0 && contentH > 0)
      setPreviewZoom(Math.min((containerW - 8) / BASE_WIDTH, (containerH - 8) / contentH));
  }, []);

  const setOuterRef = useCallback((node) => {
    if (outerRoRef.current) { outerRoRef.current.disconnect(); outerRoRef.current = null; }
    if (!node) return;
    const update = () => {
      const { width, height } = node.getBoundingClientRect();
      previewStateRef.current.containerW = width;
      previewStateRef.current.containerH = height;
      calcPreviewZoom();
    };
    const ro = new ResizeObserver(update);
    ro.observe(node);
    outerRoRef.current = ro;
    update();
  }, [calcPreviewZoom]);

  // transform:scale on this element does NOT affect offsetHeight — gives natural layout height
  const setInnerRef = useCallback((node) => {
    if (innerRoRef.current) { innerRoRef.current.disconnect(); innerRoRef.current = null; }
    if (!node) return;
    const update = () => {
      const h = node.offsetHeight;
      if (h > 0) {
        previewStateRef.current.contentH = h;
        setContentNaturalH(h);
        calcPreviewZoom();
      }
    };
    const ro = new ResizeObserver(update);
    ro.observe(node);
    innerRoRef.current = ro;
    update();
  }, [calcPreviewZoom]);

  const { pages, setPages, undo, redo, canUndo, canRedo } = useHistory([]);

  useEffect(() => {
    const snap = JSON.stringify(pages.map(cleanPage));
    setHasUnsaved(savedSnapRef.current !== null && snap !== savedSnapRef.current);
  }, [pages]);

  useEffect(() => {
    if (showJson) {
      setJsonText(buildJson(boardpackMeta, pages));
      setJsonError(null);
    }
  }, [showJson, boardpackMeta, pages]);

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === "z") {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "z"))) {
        e.preventDefault();
        redo();
      }
      if (e.key === "Escape") setSelectedPageIdx(null);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [undo, redo]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await apiCall("get", "/boardpack/contracts");
        if (res?.data) {
          const contractOptions = res.data.contracts.map((c) => ({
            label: c.description,
            value: c.description,
          }));
          setContracts(contractOptions);
        }
      } catch {
        setError("Failed to load contracts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedContract) {
      setPages([]);
      setBoardpackMeta((p) => ({ ...p, contract: "", boardpack_key: "", boardpack_name: "" }));
      savedSnapRef.current = null;
      setHasUnsaved(false);
      return;
    }
    (async () => {
      try {
        const res = await apiCall(
          "get",
          "/boardpack/config/get",
          { contract: selectedContract },
          null,
          false,
          false
        );
        if (res?.success && res?.data) {
          const d = res.data;
          setBoardpackMeta({
            boardpack_key: d.boardpack_key || "",
            boardpack_name: d.boardpack_name || "",
            contract: d.contract || selectedContract,
            status: d.status || "ACTIVE",
            created_by: d.created_by || 1,
          });
          const loaded = (d.config?.pages || []).map((p, i) => ({
            ...p,
            _id: `page_${Date.now()}_${i}`,
          }));
          setPages(loaded);
          savedSnapRef.current = JSON.stringify(loaded.map(cleanPage));
        } else {
         const contractLabel = contracts.find((c) => c.value === selectedContract)?.label || selectedContract;
setBoardpackMeta((p) => ({
  ...p,
  contract: selectedContract,
  boardpack_name: contractLabel.toUpperCase(),
  boardpack_key: contractLabel.toLowerCase().replace(/\s+/g, "_"),
}));
          setPages([]);
          savedSnapRef.current = "[]";
        }
        setSelectedPageIdx(null);
        setHasUnsaved(false);
      } catch {
        setBoardpackMeta((p) => ({ ...p, contract: selectedContract }));
        setPages([]);
        savedSnapRef.current = "[]";
      }
    })();
  }, [selectedContract]);

  // ── Page ops ──────────────────────────────────────────────────────────────
  const addPage = useCallback(
  (type) => {
    const defaults = DEFAULT_PARAMS[type] || {};
    const newPage = {
      _id: `page_${Date.now()}`,
      page_no: 0,
      type,
      title: PAGE_TYPE_MAP[type]?.label || type,
      ...defaults,
      // ✅ Fix 1: selected contract ka label auto-inject
      ...(type === "title_page" && selectedContract
        ? {
            contract: contracts.find((c) => c.value === selectedContract)?.label
              || selectedContract,
          }
        : {}),
    };
    let newIdx = 0;
    setPages((prev) => {
      const updated = [...prev, newPage].map((p, i) => ({ ...p, page_no: i + 1 }));
      newIdx = updated.length - 1;
      return updated;
    });
    setTimeout(() => setSelectedPageIdx(newIdx), 0);
  },
  [setPages, selectedContract, contracts] // ← contracts & selectedContract add karo dependency array mein
);

  const removePage = useCallback(
    (idx) => {
      setPages((prev) =>
        prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, page_no: i + 1 }))
      );
      setSelectedPageIdx(null);
    },
    [setPages]
  );

  const movePage = useCallback(
    (idx, dir) => {
      setPages((prev) => {
        const arr = [...prev];
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return arr;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        return arr.map((p, i) => ({ ...p, page_no: i + 1 }));
      });
      setSelectedPageIdx((p) => (p !== null ? p + dir : null));
    },
    [setPages]
  );

  const duplicatePage = useCallback(
    (idx) => {
      setPages((prev) => {
        const arr = [...prev];
        const clone = { ...arr[idx], _id: `page_${Date.now()}` };
        arr.splice(idx + 1, 0, clone);
        return arr.map((p, i) => ({ ...p, page_no: i + 1 }));
      });
    },
    [setPages]
  );

  const updatePageField = useCallback(
    (idx, field, value) => {
      setPages((prev) => {
        const arr = [...prev];
        if (field.includes(".")) {
          const parts = field.split(".");
          const updated = JSON.parse(JSON.stringify(arr[idx]));
          let cur = updated;
          for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
          cur[parts[parts.length - 1]] = value;
          arr[idx] = updated;
        } else {
          if (value === undefined) {
            const { [field]: _removed, ...rest } = arr[idx];
            arr[idx] = rest;
          } else {
            arr[idx] = { ...arr[idx], [field]: value };
          }
        }
        return arr;
      });
    },
    [setPages]
  );

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const handleDragStart = (idx) => {
    dragIdxRef.current = idx;
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handleDrop = (idx) => {
    const from = dragIdxRef.current;
    if (from === null || from === idx) {
      setDragOverIdx(null);
      return;
    }
    setPages((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(idx, 0, moved);
      return arr.map((p, i) => ({ ...p, page_no: i + 1 }));
    });
    setDragOverIdx(null);
    dragIdxRef.current = null;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedContract) return;
    setSaveStatus("saving");
    try {
      await apiCall("post", "/boardpack/config/save", {
        ...boardpackMeta,
        config: { pages: pages.map(cleanPage) },
      });
      savedSnapRef.current = JSON.stringify(pages.map(cleanPage));
      setHasUnsaved(false);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  // ── JSON apply ────────────────────────────────────────────────────────────
  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed.config?.pages) {
        setPages(parsed.config.pages.map((p, i) => ({ ...p, _id: `page_${Date.now()}_${i}` })));
      }
      const { config, ...meta } = parsed;
      setBoardpackMeta((p) => ({ ...p, ...meta }));
      setJsonError(null);
    } catch (e) {
      setJsonError("Invalid JSON: " + e.message);
    }
  };

  // ── Sidebar filtered list ─────────────────────────────────────────────────
  const filteredIdxs = pages.reduce((acc, page, idx) => {
    const q = sidebarSearch.toLowerCase();
    if (
      !q ||
      (page.title || "").toLowerCase().includes(q) ||
      (PAGE_TYPE_MAP[page.type]?.label || "").toLowerCase().includes(q) ||
      page.type.toLowerCase().includes(q)
    )
      acc.push(idx);
    return acc;
  }, []);

  const categoryCounts = pages.reduce((acc, p) => {
    const cat = PAGE_TYPE_MAP[p.type]?.category || "Other";
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const selectedPage = selectedPageIdx !== null ? pages[selectedPageIdx] : null;

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} />;

  return (
    <div className="bg-background border-2" style={{ zoom: "85%" }}>
      {/* Header */}
      <div className="border-b bg-card top-0 z-30 shadow-sm">
        <div className="px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Settings className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-semibold leading-none">Board Pack Configuration</h1>
                {hasUnsaved && (
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse"
                    title="Unsaved changes"
                  />
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {selectedContract
                  ? `${pages.length} pages · ${Object.entries(categoryCounts || {})
                      .map(([k, v]) => `${v} ${k}`)
                      .join(", ")}`
                  : "Select a contract to start"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Y)"
            >
              <Redo2 className="w-3.5 h-3.5" />
            </Button>

            {/* <div className="w-px h-5 bg-border mx-0.5" /> */}

             <Button
              variant="outline"
              size="sm"
              onClick={() => setShowJson(!showJson)}
              className="gap-1.5 h-7 text-xs"
            >
              <Code2 className="w-3.5 h-3.5" />
              {showJson ? "Editor" : "JSON"}
            </Button> 

            <Button
              size="sm"
              onClick={handleSave}
              disabled={saveStatus === "saving" || !selectedContract}
              className="gap-1.5 h-7 text-xs"
            >
              {saveStatus === "saving" ? (
                <>Saving…</>
              ) : saveStatus === "saved" ? (
                <>Saved</>
              ) : saveStatus === "error" ? (
                <>Error</>
              ) : (
                <>Save{hasUnsaved ? " *" : ""}</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* 3-Panel Layout */}
      <div className="flex h-[calc(100vh-57px)] overflow-hidden">

        {/* ── LEFT SIDEBAR ── */}
        {showLeftSidebar && (
          <div
            className="border-r bg-muted/20 flex flex-col flex-shrink-0 overflow-hidden"
            style={{ width: leftWidth }}
          >
            {/* Contract + meta */}
            <div className="p-3 border-b bg-card space-y-2">
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                Contract
              </label>
              <ReactSelect
                options={contracts}
                value={
                  selectedContract ? contracts.find((c) => c.value === selectedContract) : null
                }

                
                onChange={(opt) => {
                      const newContract = opt?.value || null;
                      if (newContract) {
      localStorage.setItem("selectedContract", newContract);
    } else {
      localStorage.removeItem("selectedContract");
    }

                  setSelectedContract(opt?.value || null);
                  setBoardpackMeta({
                    boardpack_key: "",
                    boardpack_name: "",
                  });
                }}
                placeholder="Select contract…"
                width="100%"
              />
              {/* {selectedContract && (
                <>
                  <StableFocusInput
                    className="w-full text-xs px-2.5 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="Boardpack name"
                    value={boardpackMeta.boardpack_name}
                    onChange={(val) => setBoardpackMeta((p) => ({ ...p, boardpack_name: val }))}
                  />
                  <StableFocusInput
                    className="w-full text-xs px-2.5 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono"
                    placeholder="Unique key (e.g. all_companies)"
                    value={boardpackMeta.boardpack_key}
                    onChange={(val) => setBoardpackMeta((p) => ({ ...p, boardpack_key: val }))}
                  />
                </>
              )} */}
            </div>

            {/* Search + category badges */}
            {selectedContract && pages.length > 0 && (
              <div className="px-3 pt-2 pb-1.5 border-b space-y-1.5">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  <StableFocusInput
                    className="w-full text-xs pl-7 pr-6 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="Filter pages…"
                    value={sidebarSearch}
                    onChange={setSidebarSearch}
                  />
                  {sidebarSearch && (
                    <button
                      onClick={() => setSidebarSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(categoryCounts).map(([cat, count]) => (
                    <span
                      key={cat}
                      className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${CATEGORY_COLORS[cat] || "bg-gray-100 text-gray-600 border-gray-200"}`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${CATEGORY_DOT[cat] || "bg-gray-400"}`}
                      />
                      {cat} <span className="opacity-60">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Page list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {!selectedContract && (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <div className="h-10 w-10 rounded-xl bg-muted border-2 border-dashed flex items-center justify-center mb-2">
                    <Settings className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">Select a contract above</p>
                </div>
              )}
              {selectedContract && pages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <div className="h-10 w-10 rounded-xl bg-muted border-2 border-dashed flex items-center justify-center mb-2">
                    <Plus className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">No pages — add one below</p>
                </div>
              )}
              {filteredIdxs.map((idx) => {
                const page = pages[idx];
                const typeInfo = PAGE_TYPE_MAP[page.type];
                const dotColor = CATEGORY_DOT[typeInfo?.category] || "bg-gray-400";
                const isSelected = selectedPageIdx === idx;
                return (
                  <div
                    key={page._id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={() => handleDrop(idx)}
                    onClick={() => setSelectedPageIdx(isSelected ? null : idx)}
                    className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-all border select-none ${
                      isSelected
                        ? "bg-primary/10 border-primary/30 shadow-sm"
                        : dragOverIdx === idx
                          ? "border-primary/40 bg-primary/5"
                          : "border-transparent hover:bg-muted hover:border-border"
                    }`}
                  >
                    <GripVertical className="w-3 h-3 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-60 cursor-grab" />
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                    <span className="text-[10px] font-mono text-muted-foreground w-4 flex-shrink-0 text-right">
                      {page.page_no}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium leading-none">
                        {typeInfo?.label || page.type}
                      </p>
                      {/* <p className="text-[10px] text-muted-foreground truncate mt-0.5 font-mono">
                        {page.type}
                      </p> */}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          movePage(idx, -1);
                        }}
                        disabled={idx === 0}
                        className="p-0.5 rounded hover:bg-muted-foreground/20 disabled:opacity-20"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          movePage(idx, 1);
                        }}
                        disabled={idx === pages.length - 1}
                        className="p-0.5 rounded hover:bg-muted-foreground/20 disabled:opacity-20"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicatePage(idx);
                        }}
                        className="p-0.5 rounded hover:bg-muted-foreground/20"
                        title="Duplicate"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removePage(idx);
                        }}
                        className="p-0.5 rounded hover:bg-red-100 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {sidebarSearch && filteredIdxs.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">
                  No pages match "{sidebarSearch}"
                </p>
              )}
            </div>

            {/* Add page */}
            {selectedContract && (
              <div className="border-t p-2.5">
                <AddPagePanel onAdd={addPage} />
              </div>
            )}
          </div>
        )}

        {/* ── LEFT RESIZE HANDLE ── */}
        {showLeftSidebar && (
          <div
            onMouseDown={startLeftResize}
            className="w-1 flex-shrink-0 cursor-col-resize bg-border/40 hover:bg-primary/50 active:bg-primary transition-colors duration-150 relative group"
            title="Drag to resize"
          >
            {/* Visual grip dots */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="w-0.5 h-0.5 rounded-full bg-primary" />
              ))}
            </div>
          </div>
        )}

        {/* ── CENTER: Preview Area ── */}
        <div className="max-h-[calc(100vh-57px)] flex-1 flex flex-col overflow-hidden bg-muted/5">
          {showJson ? (
            <JsonEditorPanel
              jsonText={jsonText}
              setJsonText={setJsonText}
              jsonError={jsonError}
              setJsonError={setJsonError}
              onApply={handleApplyJson}
              onReset={() => {
                setJsonText(buildJson(boardpackMeta, pages));
                setJsonError(null);
              }}
            />
          ) : (
            <>
              <div className="flex items-center justify-between py-2 border-b bg-card flex-shrink-0">
                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground py-0.5 px-0.5 rounded-r-sm shadow-lg transition-all duration-300"
                  onClick={() => setShowLeftSidebar(!showLeftSidebar)}
                  title={showLeftSidebar ? "Hide Left Sidebar" : "Show Left Sidebar"}
                >
                  <ChevronLeft
                    className={`w-5 h-5 transition-transform text-black-900 ${!showLeftSidebar ? "rotate-180" : ""}`}
                  />
                </button>

                {/* {selectedPage && (
                  <span className="text-md text-foreground font-medium truncate">
                    {selectedPage.title || PAGE_TYPE_MAP[selectedPage.type]?.label}
                  </span>
                )} */}

                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground py-0.5 px-0.5 rounded-l-sm shadow-lg transition-all duration-300"
                  onClick={() => setShowRightPanel(!showRightPanel)}
                  title={showRightPanel ? "Hide Right Sidebar" : "Show Right Sidebar"}
                >
                  <ChevronLeft
                    className={`w-5 h-5 transition-transform text-black-900 ${!showRightPanel ? "" : "rotate-180"}`}
                  />
                </button>
              </div>

              {!selectedPage ? (
                <div className="flex-1 overflow-auto flex items-center justify-center">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="h-14 w-14 rounded-2xl bg-muted border-2 border-dashed flex items-center justify-center mb-3">
                      <Eye className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {!selectedContract
                        ? "Select a contract to begin"
                        : pages.length === 0
                          ? "Add a page to preview it"
                          : "Select a page to preview"}
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  ref={setOuterRef}
                  className="flex-1 overflow-hidden"
                  style={{ background: "#f4f6f8", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  {/* Reserves exact visual space so no scroll or clipping */}
                  <div style={{ width: BASE_WIDTH * previewZoom, height: contentNaturalH * previewZoom, position: "relative", flexShrink: 0 }}>
                    {/* transform:scale doesn't affect layout — offsetHeight stays natural */}
                    <div
                      ref={setInnerRef}
                      style={{ width: BASE_WIDTH, transform: `scale(${previewZoom})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0 }}
                    >
                      <PagePreview page={selectedPage} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── RIGHT RESIZE HANDLE ── */}
        {showRightPanel && (
          <div
            onMouseDown={startRightResize}
            className="w-1 flex-shrink-0 cursor-col-resize bg-border/40 hover:bg-primary/50 active:bg-primary transition-colors duration-150 relative group"
            title="Drag to resize"
          >
            {/* Visual grip dots */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="w-0.5 h-0.5 rounded-full bg-primary" />
              ))}
            </div>
          </div>
        )}

        {/* ── RIGHT: Config Panel ── */}
        {showRightPanel && (
          <div
            className="flex-shrink-0 flex flex-col overflow-hidden bg-background border-l"
            style={{ width: rightWidth }}
          >
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-card flex-shrink-0">
              <SlidersHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Configuration</span>
            </div>

            <div className="flex-1 overflow-y-auto">
              {selectedPageIdx !== null && pages[selectedPageIdx] ? (
                <PageEditor
                  page={pages[selectedPageIdx]}
                  idx={selectedPageIdx}
                  totalPages={pages.length}
                  onUpdate={updatePageField}
                  onClose={() => setSelectedPageIdx(null)}
                  onRemove={() => removePage(selectedPageIdx)}
                  onMove={(dir) => movePage(selectedPageIdx, dir)}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="h-12 w-12 rounded-xl bg-muted border-2 border-dashed flex items-center justify-center mb-3">
                    <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs font-medium text-muted-foreground">
                    {!selectedContract
                      ? "No contract selected"
                      : pages.length === 0
                        ? "No pages yet"
                        : "Select a page to configure"}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Add Page Panel ───────────────────────────────────────────────────────────
const AddPagePanel = ({ onAdd }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const categories = [...new Set(PAGE_TYPES.map((p) => p.category))];
  const filtered = PAGE_TYPES.filter(
    (p) =>
      p.label.toLowerCase().includes(search.toLowerCase()) ||
      p.value.toLowerCase().includes(search.toLowerCase())
  );
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <Button
        size="sm"
        className="w-full gap-1.5 text-xs h-8"
        onClick={() => setOpen(!open)}
        variant={open ? "default" : "outline"}
      >
        <Plus className="w-3.5 h-3.5" /> Add Page
      </Button>
      {open && (
        <div className="absolute bottom-10 left-0 w-full border rounded-xl bg-card shadow-xl overflow-hidden z-50">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <StableFocusInput
                autoFocus
                className="w-full text-xs pl-7 pr-2 py-1.5 rounded-md border bg-background focus:outline-none"
                placeholder="Search page types…"
                value={search}
                onChange={setSearch}
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {categories.map((cat) => {
              const items = filtered.filter((p) => p.category === cat);
              if (!items.length) return null;
              return (
                <div key={cat}>
                  <p
                    className={`text-[10px] font-semibold px-2 py-1 uppercase tracking-widest flex items-center gap-1.5 ${CATEGORY_COLORS[cat]?.split(" ").slice(1).join(" ") || "text-muted-foreground"}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${CATEGORY_DOT[cat] || "bg-gray-400"}`}
                    />
                    {cat}
                  </p>
                  {items.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => {
                        onAdd(p.value);
                        setOpen(false);
                        setSearch("");
                      }}
                      className="w-full text-left px-3 py-1.5 rounded-lg text-xs hover:bg-muted transition-colors flex items-center gap-2"
                    >
                      <span
                        className={`text-[9px] px-1 py-0.5 rounded border font-semibold ${CATEGORY_COLORS[p.category] || ""}`}
                      >
                        {cat[0]}
                      </span>
                      <span className="">{p.label}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">No results</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── JSON Editor Panel ────────────────────────────────────────────────────────
const JsonEditorPanel = ({ jsonText, setJsonText, jsonError, setJsonError, onApply, onReset }) => (
  <div className="p-6 h-full flex flex-col">
    <div className="flex items-center justify-between mb-3">
      <div>
        <h2 className="text-sm font-semibold">JSON Editor</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Edit or paste full JSON. Click "Apply" to sync.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs h-7"
          onClick={() => navigator.clipboard.writeText(jsonText)}
        >
          <Copy className="w-3 h-3" /> Copy
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7" onClick={onReset}>
          <RotateCcw className="w-3 h-3" /> Reset
        </Button>
        <Button size="sm" className="gap-1.5 text-xs h-7" onClick={onApply}>
          <CheckCircle2 className="w-3 h-3" /> Apply
        </Button>
      </div>
    </div>
    {jsonError && (
      <div className="mb-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {jsonError}
      </div>
    )}
    <textarea
      className="flex-1 text-xs bg-card rounded-xl p-4 border font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
      value={jsonText}
      onChange={(e) => {
        setJsonText(e.target.value);
        setJsonError(null);
      }}
      spellCheck={false}
    />
  </div>
);

// ─── Page Editor ──────────────────────────────────────────────────────────────
const PageEditor = ({ page, idx, totalPages, onUpdate, onClose, onRemove, onMove }) => {
  const typeInfo = PAGE_TYPE_MAP[page.type];
  const catColor =
    CATEGORY_COLORS[typeInfo?.category] || "bg-gray-100 text-gray-700 border-gray-200";

  const INPUT_CLS =
    "w-full px-2.5 py-1.5 text-xs rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-shadow";

  return (
    <div className="p-4 space-y-4">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div
            className={`h-8 w-8 rounded-lg border-2 flex items-center justify-center text-xs font-bold ${catColor}`}
          >
            {page.page_no}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h2 className="text-xs font-semibold">{typeInfo?.label}</h2>
            </div>
            {/* <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{page.type}</p> */}
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onMove(-1)}
            disabled={idx === 0}
          >
            <ChevronUp className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onMove(1)}
            disabled={idx === totalPages - 1}
          >
            <ChevronDown className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:text-red-500"
            onClick={onRemove}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Type-specific fields */}
      <TypeSpecificFields page={page} idx={idx} onUpdate={onUpdate} />

      {/* Raw JSON */}
      {/* <RawJsonEditor page={page} idx={idx} onUpdate={onUpdate} /> */}
    </div>
  );
};

// ─── Raw JSON editor ──────────────────────────────────────────────────────────
const RawJsonEditor = ({ page, idx, onUpdate }) => {
  const [local, setLocal] = useState(() => JSON.stringify(cleanPage(page), null, 2));
  const [err, setErr] = useState(null);
  const editing = useRef(false);

  useEffect(() => {
    if (!editing.current) setLocal(JSON.stringify(cleanPage(page), null, 2));
  }, [page]);

  const apply = () => {
    try {
      const parsed = JSON.parse(local);
      Object.entries(parsed).forEach(([k, v]) => {
        if (k !== "_id") onUpdate(idx, k, v);
      });
      setErr(null);
    } catch (e) {
      setErr("Invalid JSON: " + e.message);
    }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-1.5 pt-3 px-3">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <Code2 className="w-3 h-3" /> Raw JSON
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-5 text-[10px] gap-1 px-1.5"
              onClick={() => {
                setLocal(JSON.stringify(cleanPage(page), null, 2));
                setErr(null);
              }}
            >
              <RotateCcw className="w-2.5 h-2.5" />
            </Button>
            <Button size="sm" className="h-5 text-[10px] gap-1 px-1.5" onClick={apply}>
              <CheckCircle2 className="w-2.5 h-2.5" /> Apply
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        {err && (
          <div className="mb-1.5 flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
            <AlertCircle className="w-3 h-3 flex-shrink-0" /> {err}
          </div>
        )}
        <textarea
          className="w-full font-mono text-[10px] p-2.5 rounded-lg border bg-muted min-h-28 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
          value={local}
          onFocus={() => {
            editing.current = true;
          }}
          onBlur={() => {
            editing.current = false;
          }}
          onChange={(e) => {
            setLocal(e.target.value);
            setErr(null);
          }}
          spellCheck={false}
        />
        <p className="text-[9px] text-muted-foreground mt-1">
          Edit then click "Apply" to push changes.
        </p>
      </CardContent>
    </Card>
  );
};

export default BoardPackConfigurationPage;